/**
 * Mock API Handler - Intercepts API calls and returns mock data
 *
 * Provides full CRUD functionality for all Financial Hub entities
 */
import mockDataService from './mockDataService';
import { InvoiceStatus } from '../types/invoice';
class MockApiHandler {
    /**
     * Handle mock API requests
     */
    async handleRequest(method, endpoint, data) {
        // Simulate network delay for realism
        await this.simulateNetworkDelay();
        try {
            // Route to appropriate handler
            if (endpoint.startsWith('/suppliers') || endpoint.startsWith('suppliers')) {
                return this.handleSupplierRequest(method, endpoint, data);
            }
            else if (endpoint.startsWith('/purchases') || endpoint.startsWith('purchases')) {
                return this.handlePurchaseRequest(method, endpoint, data);
            }
            else if (endpoint.startsWith('/payments') || endpoint.startsWith('payments')) {
                return this.handlePaymentRequest(method, endpoint, data);
            }
            else if (endpoint.startsWith('/invoices') || endpoint.startsWith('invoices')) {
                return this.handleInvoiceRequest(method, endpoint, data);
            }
            else if (endpoint.startsWith('/auction') || endpoint.startsWith('auction')) {
                return this.handleAuctionRequest(method, endpoint, data);
            }
            else if (endpoint.startsWith('/shipments') || endpoint.startsWith('shipments')) {
                return this.handleShipmentRequest(method, endpoint, data);
            }
            return { ok: false, error: 'Endpoint not found in mock mode' };
        }
        catch (error) {
            return { ok: false, error: error.message || 'Mock API error' };
        }
    }
    // ============================================
    // Supplier Endpoints
    // ============================================
    handleSupplierRequest(method, endpoint, data) {
        // GET /suppliers-list
        if (method === 'GET' && endpoint.startsWith('/suppliers-list')) {
            const suppliers = mockDataService.getSuppliers();
            const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
            let filtered = [...suppliers];
            // Apply filters
            const status = urlParams.get('status');
            if (status) {
                filtered = filtered.filter(s => s.status === status);
            }
            const supplierType = urlParams.get('supplierType');
            if (supplierType) {
                filtered = filtered.filter(s => s.supplierType === supplierType);
            }
            const search = urlParams.get('search');
            if (search) {
                const searchLower = search.toLowerCase();
                filtered = filtered.filter(s => s.companyName.toLowerCase().includes(searchLower) ||
                    s.supplierCode.toLowerCase().includes(searchLower) ||
                    s.email.toLowerCase().includes(searchLower));
            }
            // Calculate statistics
            const purchases = mockDataService.getPurchases();
            const statistics = {
                total: filtered.length,
                byStatus: {
                    active: filtered.filter(s => s.status === 'active').length,
                    inactive: filtered.filter(s => s.status === 'inactive').length,
                    suspended: filtered.filter(s => s.status === 'suspended').length,
                },
                totalPurchaseValue: purchases.reduce((sum, p) => sum + p.total, 0),
                totalOwed: purchases.reduce((sum, p) => sum + p.amountOwed, 0),
            };
            return {
                ok: true,
                data: {
                    suppliers: filtered,
                    total: filtered.length,
                    statistics,
                },
            };
        }
        // GET /suppliers-detail/:id
        if (method === 'GET' && endpoint.match(/\/suppliers-detail\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const supplier = mockDataService.getSupplier(id);
            if (!supplier) {
                return { ok: false, error: 'Supplier not found' };
            }
            return { ok: true, data: { supplier } };
        }
        // POST /suppliers-create
        if (method === 'POST' && endpoint === '/suppliers-create') {
            const newSupplier = mockDataService.createSupplier(data);
            return { ok: true, data: { supplier: newSupplier } };
        }
        // PUT /suppliers-update/:id
        if (method === 'PUT' && endpoint.match(/\/suppliers-update\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const updated = mockDataService.updateSupplier(id, data);
            if (!updated) {
                return { ok: false, error: 'Supplier not found' };
            }
            return { ok: true, data: { supplier: updated } };
        }
        // DELETE /suppliers-delete/:id
        if (method === 'DELETE' && endpoint.match(/\/suppliers-delete\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const success = mockDataService.deleteSupplier(id);
            if (!success) {
                return { ok: false, error: 'Supplier not found' };
            }
            return { ok: true, data: { message: 'Supplier deleted successfully' } };
        }
        return { ok: false, error: 'Supplier endpoint not found' };
    }
    // ============================================
    // Purchase Endpoints
    // ============================================
    handlePurchaseRequest(method, endpoint, data) {
        // GET /purchases-list
        if (method === 'GET' && endpoint.startsWith('/purchases-list')) {
            const purchases = mockDataService.getPurchases();
            const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
            let filtered = [...purchases];
            // Apply filters
            const supplierId = urlParams.get('supplierId');
            if (supplierId) {
                filtered = filtered.filter(p => p.supplierId === supplierId);
            }
            const paymentStatus = urlParams.get('paymentStatus');
            if (paymentStatus) {
                filtered = filtered.filter(p => p.paymentStatus === paymentStatus);
            }
            const purchaseType = urlParams.get('purchaseType');
            if (purchaseType) {
                filtered = filtered.filter(p => p.purchaseType === purchaseType);
            }
            // Calculate statistics
            const statistics = {
                total: filtered.length,
                byStatus: {
                    paid: filtered.filter(p => p.paymentStatus === 'paid').length,
                    partial: filtered.filter(p => p.paymentStatus === 'partial').length,
                    unpaid: filtered.filter(p => p.paymentStatus === 'unpaid').length,
                },
                totalValue: filtered.reduce((sum, p) => sum + p.total, 0),
                totalOwed: filtered.reduce((sum, p) => sum + p.amountOwed, 0),
            };
            return {
                ok: true,
                data: {
                    purchases: filtered,
                    total: filtered.length,
                    statistics,
                },
            };
        }
        // GET /purchases-detail/:id
        if (method === 'GET' && endpoint.match(/\/purchases-detail\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const purchase = mockDataService.getPurchase(id);
            if (!purchase) {
                return { ok: false, error: 'Purchase not found' };
            }
            return { ok: true, data: { purchase } };
        }
        // POST /purchases-create
        if (method === 'POST' && endpoint === '/purchases-create') {
            const newPurchase = mockDataService.createPurchase(data);
            return { ok: true, data: { purchase: newPurchase } };
        }
        // PUT /purchases-update/:id
        if (method === 'PUT' && endpoint.match(/\/purchases-update\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const updated = mockDataService.updatePurchase(id, data);
            if (!updated) {
                return { ok: false, error: 'Purchase not found' };
            }
            return { ok: true, data: { purchase: updated } };
        }
        // DELETE /purchases-delete/:id
        if (method === 'DELETE' && endpoint.match(/\/purchases-delete\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const success = mockDataService.deletePurchase(id);
            if (!success) {
                return { ok: false, error: 'Purchase not found' };
            }
            return { ok: true, data: { message: 'Purchase deleted successfully' } };
        }
        return { ok: false, error: 'Purchase endpoint not found' };
    }
    // ============================================
    // Payment Endpoints
    // ============================================
    handlePaymentRequest(method, endpoint, data) {
        // GET /payments-supplier-list
        if (method === 'GET' && endpoint.startsWith('/payments-supplier-list')) {
            const payments = mockDataService.getPayments();
            const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
            let filtered = [...payments];
            // Apply filters
            const supplierId = urlParams.get('supplierId');
            if (supplierId) {
                filtered = filtered.filter(p => p.supplierId === supplierId);
            }
            return {
                ok: true,
                data: {
                    payments: filtered,
                    total: filtered.length,
                },
            };
        }
        // POST /payments-supplier-create
        if (method === 'POST' && endpoint === '/payments-supplier-create') {
            const newPayment = mockDataService.createPayment(data);
            return { ok: true, data: { payment: newPayment } };
        }
        // DELETE /payments-supplier-delete/:id
        if (method === 'DELETE' && endpoint.match(/\/payments-supplier-delete\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const success = mockDataService.deletePayment(id);
            if (!success) {
                return { ok: false, error: 'Payment not found' };
            }
            return { ok: true, data: { message: 'Payment deleted successfully' } };
        }
        return { ok: false, error: 'Payment endpoint not found' };
    }
    // ============================================
    // Invoice Endpoints
    // ============================================
    handleInvoiceRequest(method, endpoint, data) {
        // GET /invoices-list
        if (method === 'GET' && endpoint.startsWith('/invoices-list')) {
            const invoices = mockDataService.getInvoices();
            const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
            let filtered = [...invoices];
            // Apply filters
            const status = urlParams.get('status');
            if (status) {
                filtered = filtered.filter(inv => inv.status === status);
            }
            const search = urlParams.get('search');
            if (search) {
                const searchLower = search.toLowerCase();
                filtered = filtered.filter(inv => inv.invoiceNumber.toLowerCase().includes(searchLower) ||
                    inv.customerName.toLowerCase().includes(searchLower) ||
                    inv.customerEmail.toLowerCase().includes(searchLower));
            }
            // Calculate statistics
            const totalValue = filtered.reduce((sum, inv) => sum + inv.total, 0);
            const paidCount = filtered.filter(inv => inv.status === 'paid').length;
            const outstandingAmount = filtered
                .filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled')
                .reduce((sum, inv) => sum + (inv.amountDue || inv.total), 0);
            return {
                ok: true,
                data: {
                    invoices: filtered,
                    total: filtered.length,
                    stats: {
                        totalInvoices: filtered.length,
                        totalValue,
                        paidCount,
                        outstandingAmount,
                    },
                },
            };
        }
        // GET /invoices-detail/:id
        if (method === 'GET' && endpoint.match(/\/invoices-detail\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const invoice = mockDataService.getInvoice(id);
            if (!invoice) {
                return { ok: false, error: 'Invoice not found' };
            }
            return { ok: true, data: { invoice } };
        }
        // PUT /invoices-update/:id
        if (method === 'PUT' && endpoint.match(/\/invoices-update\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const updated = mockDataService.updateInvoice(id, data);
            if (!updated) {
                return { ok: false, error: 'Invoice not found' };
            }
            return { ok: true, data: { invoice: updated } };
        }
        // POST /invoices-mark-paid/:id
        if (method === 'POST' && endpoint.match(/\/invoices-mark-paid\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const updated = mockDataService.updateInvoice(id, {
                status: InvoiceStatus.PAID,
                paidDate: new Date().toISOString(),
                amountPaid: mockDataService.getInvoice(id)?.total || 0,
                amountDue: 0,
            });
            if (!updated) {
                return { ok: false, error: 'Invoice not found' };
            }
            return { ok: true, data: { invoice: updated } };
        }
        // POST /invoices-cancel/:id
        if (method === 'POST' && endpoint.match(/\/invoices-cancel\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const updated = mockDataService.updateInvoice(id, {
                status: InvoiceStatus.CANCELLED,
            });
            if (!updated) {
                return { ok: false, error: 'Invoice not found' };
            }
            return { ok: true, data: { invoice: updated } };
        }
        // POST /invoices-send-email/:id (mock - just return success)
        if (method === 'POST' && endpoint.match(/\/invoices-send-email\/[^/]+$/)) {
            const id = endpoint.split('/').pop();
            const invoice = mockDataService.getInvoice(id);
            if (!invoice) {
                return { ok: false, error: 'Invoice not found' };
            }
            return {
                ok: true,
                data: {
                    message: `Invoice ${invoice.invoiceNumber} email sent to ${invoice.customerEmail}`,
                    emailSent: true,
                }
            };
        }
        return { ok: false, error: 'Invoice endpoint not found' };
    }
    // ============================================
    // Auction Endpoints
    // ============================================
    handleAuctionRequest(method, endpoint, data) {
        // Clean endpoint (strip leading slash and query string for matching)
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        const [path, queryString] = cleanEndpoint.split('?');
        const params = new URLSearchParams(queryString || '');
        // GET auction-list
        if (method === 'GET' && path === 'auction-list') {
            const auctions = mockDataService.getAuctions();
            const statusFilter = params.get('status');
            const typeFilter = params.get('type');
            const page = parseInt(params.get('page') || '1');
            const pageSize = parseInt(params.get('pageSize') || '20');
            let filtered = [...auctions];
            if (statusFilter) {
                if (statusFilter === 'active') {
                    filtered = filtered.filter((a) => a.status === 'active' || a.status === 'grace_period');
                }
                else if (statusFilter === 'scheduled') {
                    filtered = filtered.filter((a) => a.status === 'scheduled' || a.status === 'draft');
                }
                else {
                    filtered = filtered.filter((a) => a.status === statusFilter);
                }
            }
            if (typeFilter) {
                filtered = filtered.filter((a) => a.auctionType === typeFilter);
            }
            // Sort: active first, then by end time
            filtered.sort((a, b) => {
                const statusOrder = { active: 0, grace_period: 1, scheduled: 2, draft: 3, closed: 4, cancelled: 5 };
                const aOrder = statusOrder[a.status] ?? 99;
                const bOrder = statusOrder[b.status] ?? 99;
                if (aOrder !== bOrder)
                    return aOrder - bOrder;
                return new Date(a.scheduledEndTime).getTime() - new Date(b.scheduledEndTime).getTime();
            });
            const total = filtered.length;
            const totalPages = Math.ceil(total / pageSize);
            const start = (page - 1) * pageSize;
            const paginated = filtered.slice(start, start + pageSize);
            return {
                ok: true,
                data: {
                    success: true,
                    data: {
                        auctions: paginated,
                        pagination: { page, pageSize, total, totalPages },
                    },
                    timestamp: new Date().toISOString(),
                },
            };
        }
        // GET auction-detail
        if (method === 'GET' && path === 'auction-detail') {
            const auctionId = params.get('auctionId');
            if (!auctionId)
                return { ok: false, error: 'auctionId required' };
            const auction = mockDataService.getAuction(auctionId);
            if (!auction)
                return { ok: false, error: 'Auction not found' };
            const summary = mockDataService.getAuctionSummary(auctionId);
            const bids = mockDataService.getAuctionBidsForAuction(auctionId);
            return {
                ok: true,
                data: {
                    success: true,
                    data: { auction, summary, bids },
                },
            };
        }
        // GET auction-summary
        if (method === 'GET' && path === 'auction-summary') {
            const auctionId = params.get('auctionId');
            if (!auctionId)
                return { ok: false, error: 'auctionId required' };
            const summary = mockDataService.getAuctionSummary(auctionId);
            if (!summary)
                return { ok: false, error: 'Summary not found' };
            return {
                ok: true,
                data: {
                    success: true,
                    data: { summary },
                },
            };
        }
        // GET auction-my-bids
        if (method === 'GET' && path === 'auction-my-bids') {
            const page = parseInt(params.get('page') || '1');
            const pageSize = parseInt(params.get('pageSize') || '20');
            const bids = mockDataService.getMyBids();
            const total = bids.length;
            const paginated = bids.slice((page - 1) * pageSize, page * pageSize);
            return {
                ok: true,
                data: {
                    success: true,
                    data: {
                        bids: paginated,
                        pagination: { page, pageSize, total, totalPages: Math.ceil(total / pageSize) },
                    },
                },
            };
        }
        // GET auction-analytics
        if (method === 'GET' && path === 'auction-analytics') {
            const analytics = mockDataService.getAuctionAnalytics();
            return {
                ok: true,
                data: {
                    success: true,
                    data: analytics,
                },
            };
        }
        // POST auction-create
        if (method === 'POST' && path === 'auction-create') {
            const auction = mockDataService.createAuction(data);
            return {
                ok: true,
                data: {
                    success: true,
                    auctionId: auction.auctionId,
                },
            };
        }
        // PATCH auction-update
        if ((method === 'PATCH' || method === 'PUT') && path === 'auction-update') {
            const { auctionId, ...updates } = data;
            const auction = mockDataService.updateAuction(auctionId, updates);
            if (!auction)
                return { ok: false, error: 'Auction not found' };
            return {
                ok: true,
                data: { success: true, data: auction },
            };
        }
        // POST auction-cancel
        if (method === 'POST' && path === 'auction-cancel') {
            const success = mockDataService.cancelAuction(data.auctionId);
            if (!success)
                return { ok: false, error: 'Auction not found' };
            return {
                ok: true,
                data: { success: true, message: 'Auction cancelled' },
            };
        }
        // POST auction-bid
        if (method === 'POST' && path === 'auction-bid') {
            const result = mockDataService.placeBid(data.auctionId, data.amount);
            return {
                ok: result.success,
                data: result,
                error: result.error,
            };
        }
        // POST auction-watch (join/leave)
        if (method === 'POST' && path === 'auction-watch') {
            const auction = mockDataService.getAuction(data.auctionId);
            if (!auction)
                return { ok: false, error: 'Auction not found' };
            if (data.action === 'join') {
                const summary = mockDataService.getAuctionSummary(data.auctionId);
                return {
                    ok: true,
                    data: { success: true, data: { summary } },
                };
            }
            return { ok: true, data: { success: true } };
        }
        // POST auction-supervised-action
        if (method === 'POST' && path === 'auction-supervised-action') {
            const result = mockDataService.supervisedActionMock(data.auctionId, data.action, data.payload);
            return {
                ok: result.success,
                data: result,
                error: result.error,
            };
        }
        // GET auction-signalr-negotiate (return mock)
        if (method === 'GET' && path === 'auction-signalr-negotiate') {
            return {
                ok: true,
                data: { url: 'mock://signalr', accessToken: 'mock-token' },
            };
        }
        return { ok: false, error: 'Auction endpoint not found in mock mode' };
    }
    // ============================================
    // Shipment Endpoints
    // ============================================
    handleShipmentRequest(method, endpoint, data) {
        const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
        const path = endpoint.split('?')[0].replace(/^\//, '');
        // GET /shipments-list
        if (method === 'GET' && path === 'shipments-list') {
            let shipments = mockDataService.getShipments();
            const status = urlParams.get('status');
            const search = urlParams.get('search');
            if (status && status !== 'all') {
                shipments = shipments.filter((s) => s.status === status);
            }
            if (search) {
                const q = search.toLowerCase();
                shipments = shipments.filter((s) => (s.trackingNumber || '').toLowerCase().includes(q) ||
                    (s.recipient?.name || '').toLowerCase().includes(q));
            }
            return {
                ok: true,
                data: { success: true, items: shipments, totalCount: shipments.length, continuationToken: null }
            };
        }
        // GET /shipments-get?id=
        if (method === 'GET' && path === 'shipments-get') {
            const id = urlParams.get('id');
            const shipment = id ? mockDataService.getShipment(id) : null;
            if (!shipment)
                return { ok: false, error: 'Shipment not found' };
            return { ok: true, data: { success: true, shipment } };
        }
        // POST /shipments-create
        if (method === 'POST' && path === 'shipments-create') {
            const shipment = mockDataService.createShipment(data || {});
            return { ok: true, data: { success: true, shipment } };
        }
        // PUT /shipments-update?id=
        if (method === 'PUT' && path === 'shipments-update') {
            const id = urlParams.get('id');
            if (!id)
                return { ok: false, error: 'Missing id' };
            const shipment = mockDataService.updateShipment(id, data || {});
            if (!shipment)
                return { ok: false, error: 'Shipment not found' };
            return { ok: true, data: { success: true, shipment } };
        }
        // DELETE /shipments-delete?id=
        if (method === 'DELETE' && path === 'shipments-delete') {
            const id = urlParams.get('id');
            if (!id)
                return { ok: false, error: 'Missing id' };
            const deleted = mockDataService.deleteShipment(id);
            if (!deleted)
                return { ok: false, error: 'Cannot delete (not found or not draft)' };
            return { ok: true, data: { success: true, message: 'Shipment deleted' } };
        }
        // POST /shipments-import
        if (method === 'POST' && path === 'shipments-import') {
            const trackingNumbers = data?.trackingNumbers || [];
            const results = trackingNumbers.map((tn) => ({
                trackingNumber: tn,
                success: true,
                shipmentId: `ship_mock_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
                status: 'imported'
            }));
            // Also create mock shipments for each
            trackingNumbers.forEach((tn) => {
                mockDataService.createShipment({
                    trackingNumber: tn,
                    status: 'imported',
                    source: trackingNumbers.length > 1 ? 'bulk_import' : 'import',
                    recipient: { name: 'Imported Recipient', city: 'New York', state: 'NY', country: 'US', address1: '123 Main St', address2: '', zip: '10001', phone: '', email: '', company: '', labelName: '', isResidential: false, isVerified: false },
                    sender: { name: 'Royal Gems Inc', company: 'Royal Gems', address1: '47 W 47th St', address2: 'Suite 2F', city: 'New York', state: 'NY', zip: '10036', country: 'US', phone: '212-555-0100' },
                });
            });
            return { ok: true, data: { success: true, results, imported: results.length, failed: 0 } };
        }
        // POST /shipments-refresh?id=
        if (method === 'POST' && path === 'shipments-refresh') {
            const id = urlParams.get('id');
            const shipment = id ? mockDataService.getShipment(id) : null;
            if (!shipment)
                return { ok: false, error: 'Shipment not found' };
            return { ok: true, data: { success: true, shipment, previousStatus: shipment.status, newStatus: shipment.status } };
        }
        return { ok: false, error: 'Shipment endpoint not found in mock mode' };
    }
    // ============================================
    // Utility
    // ============================================
    simulateNetworkDelay() {
        const delay = Math.random() * 300 + 100; // 100-400ms
        return new Promise(resolve => setTimeout(resolve, delay));
    }
}
export default new MockApiHandler();
